import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { reportAPI, departmentAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BarChart3, TrendingUp, Users, AlertTriangle, DollarSign, Download, FileText, RefreshCw, Loader2 } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#1E3A8A', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6'];

const Reports = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [deptSpending, setDeptSpending] = useState([]);
  const [vendorSpending, setVendorSpending] = useState([]);
  const [trends, setTrends] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [financialReport, setFinancialReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('month');
  const [exporting, setExporting] = useState({});

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    fetchFinancialReport();
  }, [reportPeriod]);

  const fetchData = async () => {
    try {
      const [summaryRes, deptRes, vendorRes, trendsRes, complianceRes] = await Promise.all([
        reportAPI.getSummary(),
        reportAPI.getSpendingByDepartment(),
        reportAPI.getSpendingByVendor(null, 10),
        reportAPI.getSpendingTrends(period, null, 30),
        reportAPI.getRuleCompliance()
      ]);
      setSummary(summaryRes.data);
      setDeptSpending(deptRes.data);
      setVendorSpending(vendorRes.data);
      setTrends(trendsRes.data);
      setCompliance(complianceRes.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialReport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/export/financial-report?period=${reportPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setFinancialReport(await res.json());
      }
    } catch (err) {
      console.error('Failed to load financial report:', err);
    }
  };

  const handleExport = async (type) => {
    setExporting({ ...exporting, [type]: true });
    try {
      const endpoint = {
        transactions: '/api/export/transactions/csv',
        departments: '/api/export/departments/csv',
        beneficiaries: '/api/export/beneficiaries/csv',
        invoices: '/api/export/invoices/csv'
      }[type];

      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
      } else {
        toast.error('Failed to export data');
      }
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting({ ...exporting, [type]: false });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Reports & Analytics</h1>
            <p className="text-gray-500 mt-1">Comprehensive insights into your organization's spending</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-reports">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="financial">Financial Report</TabsTrigger>
            <TabsTrigger value="exports">Export Data</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-6">
              <DollarSign className="w-6 h-6 text-blue-600 mb-3" />
              <p className="text-gray-500 text-sm">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(summary?.total_spent)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <TrendingUp className="w-6 h-6 text-blue-500 mb-3" />
              <p className="text-gray-500 text-sm">This Month</p>
              <p className="text-2xl font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(summary?.this_month_spent)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <BarChart3 className="w-6 h-6 text-amber-500 mb-3" />
              <p className="text-gray-500 text-sm">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.total_transactions || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <Users className="w-6 h-6 text-purple-500 mb-3" />
              <p className="text-gray-500 text-sm">Active Beneficiaries</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.active_beneficiaries || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-12 gap-6">
          {/* Spending Trends */}
          <Card className="glass-card col-span-12 lg:col-span-8">
            <CardHeader className="border-b border-gray-200 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">Spending Trends</CardTitle>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32 bg-gray-50 border-gray-300 text-gray-900 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-50 border-gray-300">
                  <SelectItem value="daily" className="text-gray-900">Daily</SelectItem>
                  <SelectItem value="weekly" className="text-gray-900">Weekly</SelectItem>
                  <SelectItem value="monthly" className="text-gray-900">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-72">
                {trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: '#6B7280', fontSize: 11 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={(v) => `$${v/1000}k`} />
                      <Tooltip contentStyle={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                      <Area type="monotone" dataKey="amount" stroke="#1E3A8A" fillOpacity={1} fill="url(#colorSpend)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className="flex items-center justify-center h-full text-gray-500">No data</div>}
              </div>
            </CardContent>
          </Card>

          {/* Department Pie */}
          <Card className="glass-card col-span-12 lg:col-span-4">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900">By Department</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-48">
                {deptSpending.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={deptSpending} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="total_spent">
                        {deptSpending.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="flex items-center justify-center h-full text-gray-500">No data</div>}
              </div>
              <div className="space-y-2 mt-2">
                {deptSpending.slice(0, 4).map((d, i) => (
                  <div key={d.department_id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-gray-500">{d.department_name}</span>
                    </div>
                    <span className="text-gray-900 font-['JetBrains_Mono'] text-xs">{formatCurrency(d.total_spent)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-12 gap-6">
          {/* Top Vendors */}
          <Card className="glass-card col-span-12 lg:col-span-6">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900">Top Vendors</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {vendorSpending.length > 0 ? (
                <div className="space-y-3">
                  {vendorSpending.slice(0, 5).map((v, i) => (
                    <div key={v.beneficiary_id} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-400">#{i + 1}</span>
                        <div>
                          <p className="text-gray-900 font-medium">{v.beneficiary_name}</p>
                          <p className="text-xs text-gray-500">{v.transaction_count} transactions</p>
                        </div>
                      </div>
                      <span className="font-['JetBrains_Mono'] text-gray-900">{formatCurrency(v.total_amount)}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="py-8 text-center text-gray-500">No vendor data</div>}
            </CardContent>
          </Card>

          {/* Rule Compliance */}
          <Card className="glass-card col-span-12 lg:col-span-6">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Rule Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {compliance.length > 0 ? (
                <div className="space-y-3">
                  {compliance.slice(0, 5).map((c) => (
                    <div key={c.rule_id} className="p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-gray-900 font-medium">{c.department_name}</p>
                          <p className="text-xs text-gray-500">{c.rule_type.replace('_', ' ')}</p>
                        </div>
                        <span className={`text-sm font-medium ${c.is_approaching_limit ? 'text-amber-500' : 'text-blue-600'}`}>
                          {c.utilization_percentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${c.is_approaching_limit ? 'bg-amber-500' : 'bg-blue-900'}`}
                          style={{ width: `${Math.min(c.utilization_percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="py-8 text-center text-gray-500">No rules configured</div>}
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Financial Report Tab */}
      <TabsContent value="financial" className="space-y-6">
        <div className="flex items-center justify-between">
          <Select value={reportPeriod} onValueChange={setReportPeriod}>
            <SelectTrigger className="w-40 bg-white border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="quarter">Last 90 days</SelectItem>
              <SelectItem value="year">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {financialReport && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="glass-card">
                <CardContent className="p-6">
                  <p className="text-gray-500 text-sm">Total Balance</p>
                  <p className="text-2xl font-bold text-gray-900">{financialReport.organization?.total_balance_formatted}</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6">
                  <p className="text-gray-500 text-sm">Completed Transactions</p>
                  <p className="text-2xl font-bold text-green-600">₹{(financialReport.transactions?.total_completed / 100).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6">
                  <p className="text-gray-500 text-sm">Pending Transactions</p>
                  <p className="text-2xl font-bold text-amber-600">₹{(financialReport.transactions?.total_pending / 100).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-6">
                  <p className="text-gray-500 text-sm">Total Invoiced</p>
                  <p className="text-2xl font-bold text-gray-900">{financialReport.billing?.total_invoiced_formatted}</p>
                </CardContent>
              </Card>
            </div>

            {/* Department Utilization */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Department Budget Utilization</CardTitle>
                <CardDescription>How departments are utilizing their allocated budgets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {financialReport.departments?.map((dept) => (
                    <div key={dept.department_id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{dept.name}</p>
                          <p className="text-sm text-gray-500">
                            {dept.transactions} transactions • ₹{(dept.spent / 100).toLocaleString()} spent
                          </p>
                        </div>
                        <span className={`text-lg font-bold ${dept.utilization > 80 ? 'text-red-600' : dept.utilization > 50 ? 'text-amber-600' : 'text-green-600'}`}>
                          {dept.utilization}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${dept.utilization > 80 ? 'bg-red-500' : dept.utilization > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(dept.utilization, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  {(!financialReport.departments || financialReport.departments.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No department data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Transaction Status Breakdown */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Transaction Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {financialReport.transactions?.by_status?.map((item) => (
                    <div key={item.status} className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                      <p className="text-sm text-gray-500">{item.status}</p>
                      <p className="text-xs text-gray-400 mt-1">₹{(item.amount / 100).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Subscription Info */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Subscription Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Plan</p>
                    <p className="text-lg font-bold text-blue-900">{financialReport.subscription?.plan_name || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Status</p>
                    <p className="text-lg font-bold text-green-900">{financialReport.subscription?.status || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600">Transactions Used</p>
                    <p className="text-lg font-bold text-purple-900">
                      {financialReport.subscription?.transactions_used || 0} / {financialReport.subscription?.monthly_limit || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-600">Rollover Balance</p>
                    <p className="text-lg font-bold text-amber-900">{financialReport.subscription?.rollover_balance || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </TabsContent>

      {/* Export Tab */}
      <TabsContent value="exports" className="space-y-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Data
            </CardTitle>
            <CardDescription>Download reports and data in CSV format</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-6 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Transactions</h3>
                    <p className="text-sm text-gray-500">Export all transaction records</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleExport('transactions')}
                  disabled={exporting.transactions}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="export-transactions"
                >
                  {exporting.transactions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </div>

              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Departments</h3>
                    <p className="text-sm text-gray-500">Export department summary</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleExport('departments')}
                  disabled={exporting.departments}
                  variant="outline"
                  className="w-full border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="export-departments"
                >
                  {exporting.departments ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </div>

              <div className="p-6 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Beneficiaries</h3>
                    <p className="text-sm text-gray-500">Export all beneficiary records</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleExport('beneficiaries')}
                  disabled={exporting.beneficiaries}
                  variant="outline"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="export-beneficiaries"
                >
                  {exporting.beneficiaries ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </div>

              <div className="p-6 border border-gray-200 rounded-lg hover:border-amber-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Invoices</h3>
                    <p className="text-sm text-gray-500">Export billing invoices</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleExport('invoices')}
                  disabled={exporting.invoices}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="export-invoices"
                >
                  {exporting.invoices ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Download CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
